/**
 * Bitcoin Power Law — pure math. NO React imports; this file is unit-testable.
 *
 * ===========================================================================
 * THE CONSTANTS, AND WHY THEY ARE NOT THE ONES YOU WILL FIND ON THE INTERNET
 * ===========================================================================
 *
 * Model (fixed by spec.md): price = A x (days since the Genesis Block) ^ n,
 * with the exponent PINNED at n = 5.8.
 *
 *     FAIR  A = 1.2859e-17
 *     FLOOR A = 4.9303e-18
 *
 * --- Why not Santostasi's published constant? -----------------------------
 *
 * The widely republished Bitcoin Power Law constant is A = 1.0117e-17. That
 * value is fitted at n = 5.82, NOT at 5.8, and A and n are coupled: you cannot
 * swap one without refitting the other. Reusing 1.0117e-17 at n = 5.8 is wrong
 * by a factor of d^0.02 -- about 1.19x (19%) at today's d ~ 6,472, and the
 * error grows for every day the chart runs. So the constants below were
 * re-derived from scratch at the spec's exponent rather than copied.
 *
 * --- Derivation -----------------------------------------------------------
 *
 * Dataset:  blockchain.info /charts/market-price?timespan=all (daily close,
 *           the same series this panel plots), 5,881 points,
 *           2011-01-01 -> 2026-09-23. Pre-2011 data is excluded: BTC had no
 *           continuous market price before mid-2010 and the first months are
 *           dominated by quote noise near $0.05, which a log-space fit would
 *           weight equally with the last decade.
 *
 * Method:   fit in log10 space, where the power law is a straight line:
 *
 *               log10(price) = log10(A) + n * log10(days)
 *
 *           With n pinned at 5.8, the only free parameter is log10(A), so the
 *           fit reduces to a residual statistic over the whole series:
 *
 *               r_i        = log10(price_i) - 5.8 * log10(days_i)
 *               log10(A_fair)  = mean(r_i)      -> OLS intercept, the central
 *                                                  tendency of the data
 *               log10(A_floor) = min(r_i)       -> the true lower envelope:
 *                                                  the single worst historical
 *                                                  deviation below the model
 *
 *           FAIR  = 10^mean(r) = 1.2859e-17
 *           FLOOR = 10^min(r)  = 4.9303e-18   (the floor sits 0.4163 decades,
 *                                              i.e. 2.608x, below fair — a
 *                                              constant ratio at every date,
 *                                              since only A differs)
 *
 * --- Validation: the floor is never breached ------------------------------
 *
 * Evaluated against the four known cycle bottoms, spanning three orders of
 * magnitude. Every figure below is what FLOOR = 4.9303e-18 at n = 5.8
 * actually returns for that date (recomputed from the constants in this
 * file, not copied from anywhere), against the cycle low:
 *
 *     2015-01-14    floor $120       low $171        holds by 1.42x
 *     2018-12-15    floor $2,197     low $3,200      holds by 1.46x
 *     2020-03-13    floor $4,351     low $4,355      holds by 1.0009x
 *     2022-11-21    floor $15,189    low $15,203     holds by 1.0009x
 *
 * The floor is a lower envelope of the *fit residual*, so by construction no
 * daily close in the fitted window sits below it. The COVID crash (2020-03)
 * and the FTX bottom (2022-11) are jointly the binding constraints that set
 * min(r) — they touch the line to within 0.1% — which is exactly the property
 * a support line should have: tight, and never crossed.
 *
 * NOTE for auditors: DECISIONS.md's copy of this table has the "floor" and
 * "actual" columns transposed for the 2015 and 2018 rows (it reads as if the
 * floor sat ABOVE the low, i.e. breached). The numbers above are the ones
 * this code produces; the conclusion — never breached — is unchanged.
 *
 * --- Cross-validation -----------------------------------------------------
 *
 * FAIR predicts ~$163,300 for 2026-09-23. Santostasi's independently
 * published model gives $153,213 for the same day: agreement within 6.6% from
 * a completely separate fit, which is a meaningful check that this derivation
 * is not off by an order of magnitude or a units error.
 *
 * --- Recorded but not used ------------------------------------------------
 *
 * A free two-parameter OLS on the same data (both A and n floating) gives
 * n = 5.6033, A = 6.116e-17, i.e. $138,327 today. Noted for auditability. It
 * is NOT used, because spec.md fixes the exponent at 5.8.
 *
 * REJECTED: choosing A so the fair line passes through today's spot price
 * would give A = 6.666e-18, which puts "fair value" BELOW the 2022 cycle
 * bottom -- visibly absurd, and it would make the fair line meaningless as a
 * reference.
 *
 * --- Citations ------------------------------------------------------------
 *
 *   Giovanni Santostasi, "The Bitcoin Power Law Theory"
 *     https://giovannisantostasi.medium.com/the-bitcoin-power-law-theory-962dfaf99ee9
 *   Porkopolis Economics, "The Chart"      https://www.porkopolis.io/thechart/
 *   Newhedge, Bitcoin Power Law            https://newhedge.io/bitcoin/power-law
 *   Price series (fitted + plotted)
 *     https://api.blockchain.info/charts/market-price?timespan=all&format=json&cors=true
 *
 * ===========================================================================
 */

/** Spec-fixed exponent. Changing this INVALIDATES both constants above. */
export const POWER_LAW_EXPONENT = 5.8;

/** Fair-value coefficient. OLS intercept in log10 space at n = 5.8. */
export const POWER_LAW_FAIR_COEFFICIENT = 1.2859e-17;

/** Support coefficient. Minimum log10 residual = historical lower envelope. */
export const POWER_LAW_FLOOR_COEFFICIENT = 4.9303e-18;

/**
 * Genesis Block, 2009-01-03 18:15:05 UTC. Fixed in UTC on purpose: the model's
 * `days` term must not shift by one when the page is opened in another
 * timezone, or the whole curve moves.
 */
export const GENESIS_BLOCK_MS = Date.UTC(2009, 0, 3, 18, 15, 5);

/**
 * First day plotted / fitted. See the dataset note above -- the 2009-2010
 * pre-market period is excluded rather than plotted as a flat line at zero.
 */
export const POWER_LAW_START_MS = Date.UTC(2011, 0, 1);

/** Days elapsed since the Genesis Block. Fractional; never below 1. */
export function daysSinceGenesis(at: number | Date): number {
  const ms = at instanceof Date ? at.getTime() : at;
  if (!Number.isFinite(ms)) return 1;
  return Math.max(1, (ms - GENESIS_BLOCK_MS) / 86_400_000);
}

/** Fair-value power law in USD at `days` since genesis. */
export function powerLawFair(days: number): number {
  return POWER_LAW_FAIR_COEFFICIENT * Math.pow(Math.max(1, days), POWER_LAW_EXPONENT);
}

/** Support ("floor") power law in USD at `days` since genesis. */
export function powerLawFloor(days: number): number {
  return POWER_LAW_FLOOR_COEFFICIENT * Math.pow(Math.max(1, days), POWER_LAW_EXPONENT);
}

export interface PowerLawValues {
  readonly days: number;
  readonly fair: number;
  readonly floor: number;
}

/** Both model lines at a moment in time. */
export function powerLawAt(at: number | Date): PowerLawValues {
  const days = daysSinceGenesis(at);
  return { days, fair: powerLawFair(days), floor: powerLawFloor(days) };
}

export type PowerLawBand = 'below-floor' | 'support' | 'undervalued' | 'fair' | 'overvalued';

export interface PowerLawPosition {
  /** price / fair. 1 means exactly on the fair line. */
  readonly ratio: number;
  /** price / floor. Below 1 means the historical envelope has been breached. */
  readonly floorRatio: number;
  readonly band: PowerLawBand;
  readonly label: string;
}

const BAND_LABEL: Readonly<Record<PowerLawBand, string>> = {
  'below-floor': 'below floor',
  support: 'at support',
  undervalued: 'under model',
  fair: 'near model',
  overvalued: 'over model',
};

/**
 * Where spot sits relative to the two lines. Thresholds are deliberately wide
 * -- this is a glanceable label, not a signal.
 */
export function powerLawPosition(price: number, at: number | Date = Date.now()): PowerLawPosition {
  const { fair, floor } = powerLawAt(at);
  const ratio = price / fair;
  const floorRatio = price / floor;
  const band: PowerLawBand =
    floorRatio < 1
      ? 'below-floor'
      : floorRatio < 1.15
        ? 'support'
        : ratio < 0.85
          ? 'undervalued'
          : ratio <= 1.15
            ? 'fair'
            : 'overvalued';
  return { ratio, floorRatio, band, label: BAND_LABEL[band] };
}

/* ------------------------------------------------------------------ */
/* Chart series                                                        */
/* ------------------------------------------------------------------ */

/** One historical observation. `t` is epoch ms, `price` is USD close. */
export interface PricePoint {
  readonly t: number;
  readonly price: number;
}

/**
 * A single row for Recharts. `price` is optional on purpose: the two model
 * lines are pure math and MUST render with zero network, so the series is
 * built from the clock and the price column is merely filled in where history
 * happens to be available.
 */
export interface PowerLawChartPoint {
  readonly t: number;
  readonly fair: number;
  readonly floor: number;
  readonly price?: number;
}

export interface PowerLawChartOptions {
  /** Daily closes, ascending by `t`. Empty is fully supported. */
  readonly history?: readonly PricePoint[];
  readonly nowMs?: number;
  /** Current spot, pinned as the final price sample so the line reaches today. */
  readonly spot?: number | null;
  readonly startMs?: number;
  /** Samples across the whole window. ~190 keeps the SVG light in a 350px card. */
  readonly samples?: number;
}

/**
 * Resample onto a uniform time grid and evaluate both model lines at every
 * step. Uniform sampling (rather than plotting all ~5,900 daily closes) is
 * what keeps this readable inside a 350x190 chart area and keeps the DOM small
 * enough that a theme toggle re-render is instant.
 */
export function buildPowerLawChart(options: PowerLawChartOptions = {}): PowerLawChartPoint[] {
  const {
    history = [],
    nowMs = Date.now(),
    spot = null,
    startMs = POWER_LAW_START_MS,
    samples = 190,
  } = options;

  const end = Math.max(startMs + 86_400_000, nowMs);
  const count = Math.max(2, Math.floor(samples));
  const step = (end - startMs) / (count - 1);

  // Ascending, positive-price observations only. A log axis cannot plot 0, and
  // blockchain.info's earliest rows legitimately contain 0.
  const clean = history
    .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.price) && p.price > 0)
    .slice()
    .sort((a, b) => a.t - b.t);

  const out: PowerLawChartPoint[] = [];
  let cursor = 0;
  const tolerance = step; // accept an observation within one bucket of the grid point

  for (let i = 0; i < count; i += 1) {
    const t = i === count - 1 ? end : startMs + step * i;
    const days = daysSinceGenesis(t);

    // Two-pointer walk: advance to the last observation at or before `t`.
    while (cursor + 1 < clean.length && (clean[cursor + 1] as PricePoint).t <= t) cursor += 1;

    const candidate = clean[cursor];
    const price =
      candidate !== undefined && candidate.price > 0 && Math.abs(candidate.t - t) <= tolerance
        ? candidate.price
        : undefined;

    const row: PowerLawChartPoint = {
      t,
      fair: powerLawFair(days),
      floor: powerLawFloor(days),
      ...(price === undefined ? {} : { price }),
    };
    out.push(row);
  }

  // Pin live spot onto the final row so the price line always reaches "now",
  // even when the cached history is a few hours stale.
  const last = out[out.length - 1];
  if (spot !== null && spot > 0 && last !== undefined) {
    out[out.length - 1] = { ...last, price: spot };
  }
  return out;
}

/**
 * Decade ticks (…, 1, 10, 100, …) spanning the data. A log axis with Recharts'
 * automatic ticks produces unreadable values like 3,162; powers of ten are the
 * only labels that make a log axis legible at this size.
 *
 * 2011 -> today spans eight decades ($0.20 to $163K), which at 9px in a 175px
 * tall chart is a wall of text — so the list is thinned to `maxTicks`, keeping
 * the top decade so the axis always labels its own ceiling.
 */
export function logDecadeTicks(min: number, max: number, maxTicks = 6): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max <= 0) return [];
  const lo = Math.floor(Math.log10(min));
  const hi = Math.ceil(Math.log10(max));
  const all: number[] = [];
  for (let e = lo; e <= hi; e += 1) all.push(Math.pow(10, e));
  if (all.length <= maxTicks) return all;
  const stride = Math.ceil(all.length / maxTicks);
  // Walk backwards from the top so the highest decade survives the thinning.
  return all.filter((_, i) => (all.length - 1 - i) % stride === 0);
}

/** Log-padded [min, max] domain covering every series in the chart. */
export function chartDomain(points: readonly PowerLawChartPoint[]): [number, number] {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const p of points) {
    for (const v of [p.fair, p.floor, p.price]) {
      if (v === undefined || !Number.isFinite(v) || v <= 0) continue;
      if (v < min) min = v;
      if (v > max) max = v;
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [1, 1_000_000];
  return [Math.pow(10, Math.floor(Math.log10(min))), Math.pow(10, Math.ceil(Math.log10(max)))];
}
